import { useState } from "react";

import { CORE_PHONE_LENGTH } from "common/constants";
import { parseNumber } from "common/utils/numbers";

import { useSendVerificationCodeMutation } from "client/accounts/hooks";
import PhoneInput from "client/common/components/inputs/PhoneInput";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import { useVerifyPhoneDrawer } from "client/accounts/stackables";
import SmsLegalDisclaimer from "client/accounts/components/auth/SmsLegalDisclaimer";

import styles from "../../styles/AccountUpdatePhoneDrawer.module.less";

export type AccountUpdatePhoneDrawerProps = {
  updateClientPhone?: boolean;
  isProfileContext?: boolean;
};

const AccountUpdatePhoneDrawer = ({
  updateClientPhone = false,
  isProfileContext = false,
}: AccountUpdatePhoneDrawerProps) => {
  const [phone, setPhone] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const { open: openVerifyPhoneDrawer } = useVerifyPhoneDrawer();
  const { close } = useCurrentDrawer();

  const {
    mutate: sendVerificationCode,
    isPending: isLoading,
    error,
  } = useSendVerificationCodeMutation({
    onSuccess: () => {
      setIsVerifying(true);
    },
  });

  const handleSendCode = () => {
    sendVerificationCode({ number: parseNumber(phone, 0) });
    openVerifyPhoneDrawer({
      phone,
      updateClientPhone,
      visible: isVerifying,
      onClose: () => {
        setIsVerifying(false);
        close();
      },
      onSuccess: close,
    });
  };

  const canSubmit = phone.length === CORE_PHONE_LENGTH && !isLoading;

  const profileTitle = isProfileContext ? "Update profile phone" : "Update phone";
  const title = updateClientPhone ? "Update communications phone" : profileTitle;

  return (
    <DrawerLayout>
      <DrawerLayout.Header title={title} onClose={close} />
      <DrawerLayout.Form
        submitText="Send code"
        canSubmit={canSubmit}
        onSubmit={handleSendCode}
        error={error}
      >
        {!!error && <FetchErrorAlert error={error} />}
        <PhoneInput
          value={phone}
          label="Phone Number"
          onChange={setPhone}
          errors={error?.errors.number}
        />
        <p className={styles.help}>We&apos;ll send you a verification code via SMS.</p>
        <SmsLegalDisclaimer onLinkClick={close} />
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default AccountUpdatePhoneDrawer;
